// Who does this team take now? BACKLOG D3, DRAFT_MODES §4.2 and §4.6.
//
// One question, asked at every pick, with one answer and a reason for it. The
// two orderings a manager can submit — one overall list, or a list per position
// with a round plan — reduce here to the same question, which is why there is
// one resolver and not two engines.
//
// PRECEDENCE, STATED ONCE (§4.6). At every pick, for the team on the clock:
//
//   1. the in-room QUEUE, if this is a live draft and the queue is not empty
//   2. the manager's PLAN, filtered to positions allowed right now
//   3. the HOUSE ranking
//
// Every step subject to the caps and the exclusion list. Sleeper has only a
// queue, ESPN and Yahoo have only a list, and we have both — so which one was
// used is carried out on the answer (`by`) rather than left to be guessed. It
// goes in the recap and in the plain-English describer: nobody should have to
// wonder which one the engine took.
//
// UNRANKED IS NOT EXCLUDED (§4.5). A player left off a manager's list still
// gets drafted to them, after everyone they ranked, subject to the caps. This
// is deliberately the one place in this project where *fail loudly* is the
// wrong instinct: an empty plan is not schema drift, it is somebody who was
// busy that week, and the draft still has to hand them a team they are not
// embarrassed by.

/** A manager's plan, as much of it as the resolver needs. */
export interface ResolvePlan {
  ordering: 'overall' | 'by-position';
  overall: readonly string[];
  byPosition: Readonly<Record<string, readonly string[]>>;
  limits: Readonly<Record<string, { min: number; max: number }>>;
  roundPlan: readonly string[];
  doNotDraft: readonly string[];
  /** In an auction league, the most this manager would pay for each player they priced (AUCTION.md §7). */
  prices?: Readonly<Record<string, number>>;
}

export interface ResolveInput {
  /** Null when this manager never opened a plan. They get the house list and the defaults. */
  plan: ResolvePlan | null;
  /** The in-room queue, newest intent first. Empty outside a live draft. */
  queue: readonly string[];
  /** The house ranking, best first. The floor under every plan. */
  house: readonly string[];
  /** Everybody already drafted, by anybody. */
  taken: ReadonlySet<string>;
  /** This team's picks so far. */
  roster: readonly string[];
  /** 1-based. Indexes `roundPlan`. */
  round: number;
  /** Picks this team still has, INCLUDING this one. */
  picksLeft: number;
  positionOf: (playerId: string) => string;
  /**
   * THE LEAGUE'S OWN CAPS (`roster.maxPerPosition`), which no plan may exceed.
   *
   * A plan's `max` is the manager's choice and is theirs to raise; the
   * commissioner's is the rules, and rules are data (CLAUDE.md). The first
   * version read the plan's limits alone, so a plan of five running backs with
   * `RB {max: 99}` drafted five against a league cap of two, and the roster
   * was illegal by the same function the waiver wire uses
   * (AUDIT-2026-09-23 RD3). The effective cap is the smaller of the two.
   */
  caps?: Readonly<Record<string, number>>;
}

export interface Resolved {
  playerId: string | null;
  /** Which source produced it. Stored on the pick, shown in the recap. */
  by: 'queue' | 'plan' | 'house' | 'none';
  /** One sentence, for the recap and for anybody debugging a draft at 2am. */
  why: string;
}

const EMPTY: Resolved = { playerId: null, by: 'none', why: 'Nobody left to take.' };

/** How many of each position this team already holds. */
function counts(
  roster: readonly string[],
  positionOf: (id: string) => string,
): Record<string, number> {
  const have: Record<string, number> = {};
  for (const id of roster) {
    const p = positionOf(id);
    have[p] = (have[p] ?? 0) + 1;
  }
  return have;
}

/**
 * What a pass of the resolver is allowed to ignore. See `resolvePick`.
 *
 * Ordered by how much each costs the manager: an unmet minimum is a hole a
 * waiver claim fills; a cap exceeded is a bench spot spent; a ban broken is
 * the one thing they said never. The league's own caps are never relaxed —
 * they are rules, not intent.
 */
export interface Relaxation {
  minimums: boolean;
  planLimits: boolean;
  bans: boolean;
}
const NOTHING_RELAXED: Relaxation = { minimums: false, planLimits: false, bans: false };
const RELAXATIONS: readonly { relax: Relaxation; why: string }[] = [
  {
    relax: { minimums: true, planLimits: false, bans: false },
    why: 'everybody who could fill your minimum was on your do-not-draft list or over a cap, so the minimum went unmet',
  },
  {
    relax: { minimums: true, planLimits: true, bans: false },
    why: 'your limits left nobody to take, so this pick ignored them',
  },
  {
    relax: { minimums: true, planLimits: true, bans: true },
    why: 'everybody left was on your do-not-draft list, so this pick ignored it',
  },
];

/**
 * Which positions this team may take right now, and why.
 *
 * THE THREE RULES OF §4.2 LIVE HERE, and they are the whole substance of the
 * story:
 *
 *   1. `max` IS A VETO, NEVER A NUDGE. A position at its cap is removed from
 *      consideration. Nothing in here ever reaches FOR a position to satisfy
 *      something — that is the difference between a cap and a quota, and it is
 *      what stops a plan drafting a kicker in round 6.
 *
 *   2. `min` IS SATISFIED AS LATE AS IT STILL CAN BE. A minimum reserves the
 *      LAST picks that could fill it, never an early one. So the minimums only
 *      start constraining once there are exactly as many picks left as unmet
 *      minimums — at which point every remaining pick is spoken for.
 *
 *   3. A LIMIT NEVER BECOMES A REASON TO DRAFT SOMEBODY EARLY. This falls out
 *      of rule 2 and it is the one that matters: while there is still room,
 *      **the manager's list wins**. Yahoo is the cautionary case — filling
 *      starters before bench reliably produces a defense in round 6 and five
 *      kickers after it, and the standard player workaround is to exclude every
 *      kicker so the balancer has nothing to reach for.
 */
export function allowedPositions(
  input: ResolveInput,
  relax: Relaxation = NOTHING_RELAXED,
): {
  allowed: (position: string) => boolean;
  forced: boolean;
} {
  const limits = relax.planLimits ? {} : (input.plan?.limits ?? {});
  const have = counts(input.roster, input.positionOf);

  const underCap = (position: string): boolean => {
    const mine = limits[position]?.max;
    const league = input.caps?.[position];
    const cap = Math.min(mine ?? Infinity, league ?? Infinity);
    return (have[position] ?? 0) < cap;
  };

  // Rule 2: what is still owed, and whether the remaining picks can cover it.
  let owed = 0;
  const short = new Set<string>();
  for (const [position, limit] of Object.entries(limits)) {
    const missing = limit.min - (have[position] ?? 0);
    if (missing > 0) {
      owed += missing;
      short.add(position);
    }
  }

  // The moment the minimums bite: every pick left is now spoken for. Before
  // this, they do not constrain anything at all (rule 3).
  const forced = !relax.minimums && owed > 0 && input.picksLeft <= owed;
  return {
    allowed: (position) => underCap(position) && (!forced || short.has(position)),
    forced,
  };
}

/**
 * The manager's own pick for this round, from their plan, or undefined.
 *
 * `by-position` needs the round plan to say WHICH list to read. `overall` is
 * one list and the round plan is a filter over it — which is the part the
 * first version missed: it read the intent only for `by-position`, so a
 * manager on an overall list who wrote "round 3: TE" was quietly ignored.
 */
function fromPlan(
  plan: ResolvePlan,
  round: number,
  house: readonly string[],
  usable: (id: string) => boolean,
): string | undefined {
  if (plan.ordering !== 'by-position') return plan.overall.find(usable);
  const intent = plan.roundPlan[round - 1];
  if (intent !== undefined && intent !== 'best') {
    return (plan.byPosition[intent] ?? []).find(usable);
  }
  // "BEST AVAILABLE" FROM LISTS THAT HAVE NO ORDER BETWEEN THEM. Each list
  // offers its next usable player — the manager's order within a position is
  // theirs — and of those the house ranking picks the highest, because it is
  // the only order across positions there is. Until 2026-09-24 this read the
  // lists end to end in the order the positions were written, so "best
  // available" meant "the first position's list first": a plan whose QB list
  // came first took a quarterback in every open round (PLAN-2026-09-24, the
  // by-position screen). A head the house does not rank comes after any it does.
  const rank = new Map(house.map((id, i) => [id, i]));
  let best: string | undefined;
  let bestRank = Number.POSITIVE_INFINITY;
  for (const list of Object.values(plan.byPosition)) {
    const head = list.find(usable);
    if (head === undefined) continue;
    const r = rank.get(head) ?? Number.MAX_SAFE_INTEGER;
    if (best === undefined || r < bestRank) {
      best = head;
      bestRank = r;
    }
  }
  return best;
}

export function resolvePick(input: ResolveInput): Resolved {
  const intent = input.plan?.roundPlan[input.round - 1];
  const wanted = intent !== undefined && intent !== 'best' ? intent : null;

  const strict = resolveWith(input, NOTHING_RELAXED, true, wanted);
  if (strict) return strict;
  if (wanted !== null) {
    const relaxed = resolveWith(input, NOTHING_RELAXED, false, wanted);
    if (relaxed) return relaxed;
  }

  // A PLAN CAN MAKE A TEAM WORSE. IT CANNOT STOP THE LEAGUE. The first
  // version returned `EMPTY` here, and the runner then refused the whole
  // draft: a plan that banned every kicker under the default `K {min: 1}`, or
  // named a position the sport does not have, or capped every position below
  // the round count, left the league's draft stuck at `live` on the night —
  // every seat's, not only the planner's — with a ledger row nobody read
  // (AUDIT-2026-09-23 RD1). So the plan's constraints are let go one at a
  // time, cheapest first, and the answer says which one gave. The league's
  // caps are never let go, and `EMPTY` is only ever "the pool is empty".
  for (const { relax, why } of RELAXATIONS) {
    const found = resolveWith(input, relax, false, null);
    if (found) return { ...found, why: `${found.why.replace(/\.$/, '')} — ${why}.` };
  }
  return EMPTY;
}

/**
 * One pass: the queue, then the plan, then the house, under one relaxation.
 * Null when nothing in the whole board is allowed under it.
 */
function resolveWith(
  input: ResolveInput,
  relax: Relaxation,
  restrict: boolean,
  wanted: string | null,
): Resolved | null {
  const { plan, taken, positionOf } = input;
  const excluded = new Set(relax.bans ? [] : (plan?.doNotDraft ?? []));
  const { allowed, forced } = allowedPositions(input, relax);

  // `doNotDraft` is ABSOLUTE and is checked before anything else. A manager who
  // says never wants never — Yahoo's exclusion list is the feature people
  // already abuse as a substitute for caps, and it only stays honest if it is
  // never overridden by a queue, a plan or a shortage. It gives only on the
  // last rung of the ladder above, when it is the only thing left standing
  // between the league and a draft that cannot finish.
  const open = (id: string): boolean =>
    !taken.has(id) && !excluded.has(id) && allowed(positionOf(id));

  const forcedNote = forced ? ' — every pick left is reserved for a minimum' : '';

  /**
   * TWO PASSES, AND THE SECOND ONE IS NOT A LOOPHOLE. The round plan is the
   * manager's intent for this round, so it filters everything — the plan and
   * the house alike, or "round 3: TE" would be abandoned the moment their own
   * TE list ran dry. But it is WEAKER THAN A CAP (§4.3: it constrains *when*,
   * not *how many*), and a round nobody can satisfy must not stall the draft.
   * So: try it, and if nothing in the whole board satisfies it, take the best
   * available instead and say that is what happened.
   */
  {
    const usable = (id: string): boolean =>
      open(id) && (!restrict || wanted === null || positionOf(id) === wanted);
    const note = restrict && wanted !== null ? ` (round plan: ${wanted})` : '';
    const missed =
      !restrict && wanted !== null ? ` — no ${wanted} was available for this round` : '';

    // 1. The queue. A live-draft intent expressed seconds ago beats a list
    //    written on Tuesday, which is the only sensible reading of somebody
    //    queueing a player while watching the board.
    const queued = input.queue.find(usable);
    if (queued !== undefined) {
      return {
        playerId: queued,
        by: 'queue',
        why: `From your queue${note}${missed}${forcedNote}.`,
      };
    }

    // 2. The plan.
    if (plan) {
      const planned = fromPlan(plan, input.round, input.house, usable);
      if (planned !== undefined) {
        return {
          playerId: planned,
          by: 'plan',
          why: `Highest on your list${note}${missed}${forcedNote}.`,
        };
      }
    }

    // 3. The house. `find`, not a filter: a manager who ranked nobody, or who
    //    ran out of list, still gets a real team (§4.5).
    const fromHouse = input.house.find(usable);
    if (fromHouse !== undefined) {
      return {
        playerId: fromHouse,
        by: 'house',
        why: plan
          ? `Nobody on your list was available${note}${missed}, so the house ranking${forcedNote}.`
          : `You did not rank anybody, so the house ranking${note}${missed}${forcedNote}.`,
      };
    }
    return null;
  }
}
